import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Collapse, useTheme, Stack } from '@mui/material';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from '@mui/x-data-grid-premium';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useFullscreen } from './FullscreenContainer';

export const CustomToolbar = ({ 
    isPinned, 
    setIsPinned, 
    pinNeeded = false, 
    modalOpen, 
    setModalOpen, 
    modalNeeded = false, 
    exportFileName = 'DatagridExport',
    columns = [],
    csvOptions
}) => {
    const [expanded, setExpanded] = useState(true);
    const [isCompact, setIsCompact] = useState(false);
    const theme = useTheme();
    const toolbarRef = useRef(null);
    
    // Use the fullscreen context
    const { isFullScreen, toggleFullScreen } = useFullscreen();

    useEffect(() => {
        const checkWidth = () => {
            const gridElement = toolbarRef.current?.closest('.MuiDataGrid-root')?.parentElement;
            if (gridElement) {
                const width = gridElement.getBoundingClientRect().width;
                setIsCompact(width < 400);
            }
        };
        
        // Check initially
        checkWidth();
        
        // Add resize listener
        window.addEventListener('resize', checkWidth);
        
        // Clean up
        return () => {
            window.removeEventListener('resize', checkWidth);
        };
    }, []);

    const handleToggle = () => {
        setExpanded((prev) => !prev);
    };

    const handleExport = () => {
        const processedColumns = columns.map(col => {
            if (col.type === 'date') {
                return {
                    ...col,
                    valueFormatter: (params) => {
                        if (!params.value) return '';
                        const date = params.value instanceof Date 
                            ? params.value 
                            : new Date(params.value);
                        if (isNaN(date)) return params.value.toString();
                        return date.toISOString().split('T')[0];
                    }
                };
            }
            return col;
        });

        return {
            csv: {
                fileName: `${exportFileName}.csv`,
                delimiter: ',',
                utf8WithBom: true,
                escapeFormulas: false,
                ...csvOptions
            },
            excel: {
                fileName: `${exportFileName}.xlsx`,
            },
            columns: processedColumns
        };
    };

    const renderToolbarContent = () => {
        if (isCompact) {
            return (
                <GridToolbarFilterButton
                    sx={{ fontWeight: 500 }}
                    slotProps={{
                        button: {
                            startIcon: <TuneRoundedIcon />,
                            sx: { fontWeight: 500 }
                        }
                    }}
                />
            );
        }

        return (
            <>
                <GridToolbarColumnsButton
                    slotProps={{
                        button: {
                            startIcon: <ViewWeekOutlinedIcon />,
                            sx: { fontWeight: 500 }
                        }
                    }}
                />
                <GridToolbarFilterButton
                    sx={{ fontWeight: 500 }}
                    slotProps={{
                        button: {
                            startIcon: <TuneRoundedIcon />,
                            sx: { fontWeight: 500 }
                        }
                    }}
                />
                <GridToolbarDensitySelector />
                <GridToolbarExport 
                    printOptions={{ 
                        disableToolbarButton: true 
                    }}
                    csvOptions={handleExport().csv}
                    excelOptions={handleExport().excel}
                />
            </>
        );
    };

    return (
        <GridToolbarContainer
            ref={toolbarRef}
            sx={{
                background: theme.palette.customColors.mediumBackground,
                borderBottom: '1px solid #e0e0e0',
                justifyContent: 'space-between',
                '& .MuiButton-root': {
                    background: 'transparent',
                    color: '#0048E5 !important',
                },
                alignItems: 'center'
            }}
        >
            <Stack direction="row" sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {!isCompact && (
                        <Tooltip title={expanded ? "Close menu" : "Open menu"}>
                            <IconButton onClick={handleToggle} size="large" sx={{ px: 1, py: 0 }}>
                                <MenuOpenIcon sx={{ fontSize: 21, p: 0 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Collapse in={expanded} orientation="horizontal">
                        <Box sx={{ display: 'flex', gap: 1, fontWeight: 500 }}>
                            {renderToolbarContent()}
                            {pinNeeded && (
                                <Tooltip title={isPinned ? "Unpin Rows" : "Pin Rows"}>
                                    <IconButton onClick={() => setIsPinned(!isPinned)} size="small">
                                        {isPinned ? <PushPinIcon sx={{ fontSize: 18, p: 0 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18, p: 0 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {modalNeeded && (
                                <Tooltip title="Open Table Modal">
                                    <IconButton onClick={() => setModalOpen(!modalOpen)} size="small">
                                        <FindInPageOutlinedIcon sx={{ fontSize: 18, p: 0 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Collapse>
                </Box>

                <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                    <IconButton onClick={toggleFullScreen} size="small" sx={{ alignSelf: 'flex-end' }}>
                        {isFullScreen ? 
                            <FullscreenExitIcon sx={{ fontSize: 21, p: 0 }} /> : 
                            <FullscreenIcon sx={{ fontSize: 21, p: 0 }} />
                        }
                    </IconButton>
                </Tooltip>
            </Stack>
        </GridToolbarContainer>
    );
};

export default CustomToolbar;